import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PixelHourglass } from '../components/PixelHourglass';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, signOut } = useAuth();
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [checkingApproval, setCheckingApproval] = useState(true);

  useEffect(() => {
    const checkApprovalStatus = async () => {
      if (!user) {
        setCheckingApproval(false);
        return;
      }

      try {
        // Check approval status from users table
        const { data, error } = await supabase
          .from('users')
          .select('approval_status')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking approval status:', error);
          setCheckingApproval(false);
          return;
        }

        setApprovalStatus(data?.approval_status || 'approved');
      } catch (error) {
        console.error('Error in approval check:', error);
      } finally {
        setCheckingApproval(false);
      }
    };

    checkApprovalStatus();
  }, [user]);

  if (loading || checkingApproval) {
    return (
      <div
        className="min-h-screen bg-[#ffffeb] pb-24 flex items-center justify-center"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
            repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
          `
        }}
      >
        <PixelHourglass size="large" speed="fast" color="keppel" data-id="budget-status-loader" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user's approval status is not approved
  if (approvalStatus === 'pending') {
    // Sign out and redirect to waitlist
    signOut();
    return <Navigate to="/waitlist-confirmation" replace />;
  }

  if (approvalStatus === 'rejected') {
    // Sign out and redirect to login
    signOut();
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
